import { memo, useCallback, useEffect, useState } from 'react'
import { LoadingSection } from '@quickmd-llc/design-system'
import { useLocation, useNavigate } from 'react-router-dom'
import { MANAGEMENT_PAGE_ROUTE } from '../../../../utils/constants'
import CreateEditIntakeView from '../components/create-edit-intake-form-view/CreateEditIntakeView'
import type { IntakeFormRequestBody } from '../components/create-edit-intake-form-view/types'
import { useCreateIntakeForm } from '../hooks/useCreateIntakeForm'
import { useErrorToast } from '@/hooks/useErrorToast'

/**
 * Create-intake-form route shell (route `/management/new-intake-form`).
 *
 * - Renders <CreateEditIntakeView> with `isEdit` falsy (create mode).
 * - `isPending` from useCreateIntakeForm drives the inline DS LoadingSection
 *   (replaces the former ui_kit_qmd PageLoadingSpinner — medications/ViewIntakeForm pattern).
 * - Errors surface through useErrorToast inside the error effect (replaces the former
 *   toastErrorMessGenerator(error) inline render — same trigger conditions, Task-5 precedent).
 * - Success / cancel navigate back to MANAGEMENT_PAGE_ROUTE (preserving location.search).
 */
const CreateNewIntakeForm = memo(() => {
  const navigate = useNavigate()
  const location = useLocation()

  const { createIntakeForm, isPending: loading } = useCreateIntakeForm()
  const [error, setError] = useState('')

  const toastError = useErrorToast()

  const onCancelBtnPress = useCallback(() => {
    setError('')
    navigate(`${MANAGEMENT_PAGE_ROUTE}${location.search || ''}`)
  }, [navigate, location.search])

  useEffect(() => {
    if (error) {
      toastError(error)
      const timeoutId = setTimeout(() => {
        setError('')
      }, 3500)
      return () => {
        if (timeoutId) {
          window.clearTimeout(timeoutId)
        }
      }
    }
    return undefined
  }, [error, toastError])

  const createNewIntakeForm = useCallback(
    (form: IntakeFormRequestBody) => {
      createIntakeForm(form, {
        onSuccess: () => {
          void navigate(`${MANAGEMENT_PAGE_ROUTE}${location.search || ''}`)
        },
        onError: (message) => setError(message),
      })
    },
    [createIntakeForm, navigate, location.search],
  )

  const setIntakeFormError = useCallback((errorMsg: string) => {
    setError(errorMsg)
  }, [])

  return (
    <div className='flex flex-1 flex-col'>
      {loading ?
        <div className='flex h-screen items-center justify-center'>
          <LoadingSection />
        </div>
      : <CreateEditIntakeView
          error={error}
          createNewIntakeForm={createNewIntakeForm}
          onCancelBtnPress={onCancelBtnPress}
          setIntakeFormError={setIntakeFormError}
        />
      }
    </div>
  )
})

export default CreateNewIntakeForm
